import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
// Lucide Icons
import {
  LucideAngularModule,
  GripVertical,
  Trash2,
  Plus,
  ChevronDown,
} from 'lucide-angular';
import {
  SurveyListOptionService,
} from '../../survey-list-option/survey-list-option';
import {
  AutoSaveFormBase,
  FormGroupControlsOf,
} from '../../base-classes/base-form.class';
import { firstValueFrom } from 'rxjs';
import { SurveyListOption } from '@hyper-inferno/survey-elements-core';
import { AddBulkOptionsDialog } from './add-bulk-options-dialog/add-bulk-options-dialog';

@Component({
  selector: 'lib-list-option-manager',
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatIconModule,
    LucideAngularModule,
    ReactiveFormsModule,
    MatDialogModule,
  ],
  templateUrl: './list-option-manager.html',
  styleUrl: './list-option-manager.scss',
})
export class ListOptionManager extends AutoSaveFormBase<SurveyListOption> {
  override entityId = computed(() => this.activeItemId());
  override entityName = 'survey_list_option';
  activeListId = input.required<string | undefined>();
  private listOptionService = inject(SurveyListOptionService);
  readonly items = signal<SurveyListOption[]>([]);
  activeItemId = signal<string | number | null>(null);
  readonly hasChanges = output<boolean>();
  private initialItems: SurveyListOption[] = [];
  override form = this.fb.group<FormGroupControlsOf<SurveyListOption>>({
    id: this.fb.control<any | undefined>(undefined),
    list_id: this.fb.control<any | undefined>(undefined),
    option_key: this.fb.control<number>(0),
    option_display: this.fb.control<any | undefined>(undefined),
    is_open_ended: this.fb.control<boolean>(false),
    is_mutually_exclusive: this.fb.control<boolean>(false),
    simple_props: this.fb.control<any>({})
  });

  // Icons
  readonly GripIcon = GripVertical;
  readonly TrashIcon = Trash2;
  readonly PlusIcon = Plus;
  readonly ChevronDownIcon = ChevronDown;
  protected override rebuildFormArrays(data: any): void {
    if (data?.id) {
      this.form.patchValue({ id: data.id }, { emitEvent: false });
    }
  }
  constructor() {
    super();

    // Fetch with listLid

    // Effect to fetch items when the list ID changes
    effect(async () => {
      const listId = this.activeListId();
      if (listId) {
        try {
          await this.reloadItems(listId);
          const response = await firstValueFrom(
            this.listOptionService.getListOptions(listId),
          );
          // Sort by rank before setting
          const sortedItems = (response || []).sort(
            (a, b) => (a.option_key ?? 0) - (b.option_key ?? 0),
          );
          this.items.set(sortedItems);
        } catch (error) {
          console.error('Error fetching options:', error);
        }
      } else {
        this.items.set([]);
        this.initialItems = [];
      }
    });
  }
  // options
  async addItem(isExclusive = false, isOpenEnded = false) {
    const listId = this.activeListId();

    if (!listId) {
      console.error('Cannot add item: No active list ID found.');
      return;
    }

    const newItem: SurveyListOption = {
      list_id: listId,
      option_display: this.getDefaultText(isExclusive, isOpenEnded) ?? '',
      is_open_ended: isOpenEnded,
      is_mutually_exclusive: isExclusive,
      option_key: this.items().length + 1,
      id: ''
    };

    try {
      const response = await firstValueFrom(
        this.listOptionService.createListOption({
          list_id: listId,
          option_key: newItem.option_key ?? 0,
          option_display: newItem.option_display ?? '',
          is_mutually_exclusive: newItem.is_mutually_exclusive ?? false,
          is_open_ended: newItem.is_open_ended ?? false,
        }),
      );

      if (response?.id) {
        newItem.id = response.id;
        this.items.update((prev) => {
          const updated = [...prev, newItem];
          return updated;
        });
        this.listOptionService.notifyListUpdated(listId); 
        this.checkForChanges();
      }
    } catch (error) {
      console.error('Error creating option:', error);
    }
  }

  async removeItem(id: string) {
    try {
      const payload = {
        id: id,
        list_id: this.activeListId() as string,
      };

      await firstValueFrom(this.listOptionService.deleteListOption(payload));
      this.items.update((prev) => prev.filter((item) => item.id !== id));
      this.listOptionService.notifyListUpdated(this.activeListId()!);
      this.checkForChanges();
    } catch (error) {
      console.error('Error deleting option:', error);
    }
  }

  private getDefaultText(
    isExclusive: boolean,
    isOpenEnded: boolean,
  ): string | null {
    if (isExclusive) {
      return 'None of the above';
    } else if (isOpenEnded) {
      return 'Other (please specify)';
    } else {
      return null;
    }
  }

  async drop(event: CdkDragDrop<SurveyListOption[]>) {
    // 1. Move the item in the local array
    const currentItems = [...this.items()];
    moveItemInArray(currentItems, event.previousIndex, event.currentIndex);

    // 2. Re-calculate option_keys based on new array positions
    const updatedItems = currentItems.map((item, index) => ({
      ...item,
      option_key: index + 1, // Using 1-based indexing for option_key
    }));

    // 3. Update local Signal immediately for UI responsiveness
    this.items.set(updatedItems);
    this.checkForChanges(); 
    // 4. Persist to backend
    try {
      // Send only the necessary data to the service
      const option_keyPayload = updatedItems.map((i) => ({
        id: i.id!,
        option_key: i.option_key!,
        listId: this.activeListId()!,
      }));
      await firstValueFrom(
        this.listOptionService.updateOptionRanks(option_keyPayload),
      );
      this.listOptionService.notifyListUpdated(this.activeListId()!)
    } catch (error) {
      console.error('Failed to update option_keys:', error);
      // Optional: Fetch the original list again to revert the UI on failure
    }
  }
  setActiveOption(item: SurveyListOption) {
    // Set the active item ID
    this.activeItemId.set(item.id ?? null);
    // Patch the form so AutoSaveFormBase knows which record is being edited
    this.form.patchValue({
      id: item.id,
      list_id: item.list_id,
      option_display: item.option_display,
      is_open_ended: item.is_open_ended,
      is_mutually_exclusive: item.is_mutually_exclusive,
      option_key: item.option_key,
    });
    // focus
    requestAnimationFrame(() => {
      const input = document.querySelector(
        `input[formControlName="option_display"][data-item-id="${item.id}"]`,
      ) as HTMLInputElement;

      if (input) {
        input.focus();
        // Place cursor at the end
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    });
  }

  clearActiveOption() {
    this.activeItemId.set(null);
  }

  override PostCreate() {
    // 1. Get the latest data from the form (which now has the server-provided ID)
    const savedItem = this.form.getRawValue() as SurveyListOption;

    if (savedItem.id) {
      // 2. Update the signal so the UI reflects the "Saved" state and the real ID
      this.items.update((currentItems) =>
        currentItems.map((item) => {
          // If we find an item without an ID (the one we just added),
          // or if we match by some other temporary key, swap it.
          // Usually, the 'active' item is the one we just created.
          if (
            item.id === null ||
            item.id === undefined ||
            item.id === savedItem.id
          ) {
            return { ...item, ...savedItem };
          }
          return item;
        }),
      );

      // 3. Set this as the active item so the user can keep typing
      this.activeItemId.set(savedItem.id);
      this.checkForChanges();
    }
  }

  async handleEnterKey(event: any) {
    event.preventDefault(); //  stops default button trigger
    event.stopPropagation(); //  stops bubbling
    // 1. Force the current form to save immediately
    await this.bypassDebounceAndSave();
    this.addItem(false, false).then(() => {
      // Recursive function to attempt focusing the new item
      const tryToFocus = () => {
        // Get all current items
        const items = this.items();
        // New item is always added at the end of the array
        const newItem = items[items.length - 1];
        // Guard clause: exit if no new item found
        if (!newItem) return;
        // Make the new item the active/editable item in the component state
        this.setActiveOption(newItem);
        // Force Angular to detect changes and update the DOM immediately
        this.cdr.detectChanges();
        // Small delay to allow Angular to render the DOM after change detection
        setTimeout(() => {
          // Try to find the editable input (the one with formControlName)
          // This input appears only when the item is active
          const editableInput = document.querySelector(
            `input[formControlName="option_display"][data-item-id="${newItem.id}"]`,
          ) as HTMLInputElement;

          // If editable input exists, focus it and place cursor at the end
          if (editableInput) {
            editableInput.focus();
            // Set cursor position to the end of the input text
            editableInput.setSelectionRange(
              editableInput.value.length,
              editableInput.value.length,
            );
            return; // Success - exit the function
          }

          // If editable input not found, find the readonly input
          const readonlyInput = document.querySelector(
            `input[data-item-id="${newItem.id}"]`,
          ) as HTMLInputElement;

          // If readonly input exists, trigger focus and click to make it editable
          if (readonlyInput) {
            // Dispatch focus event to trigger the (focus) event handler in template
            readonlyInput.dispatchEvent(
              new FocusEvent('focus', { bubbles: true }),
            );
            // Trigger click to ensure the input becomes active
            readonlyInput.click();

            setTimeout(() => tryToFocus(), 100);
          }
        }, 0);
      };
      // Initial call to start the focusing process after item creation
      setTimeout(() => tryToFocus(), 100);
    });
  }

  private checkForChanges() {
    const currentItems = this.items();
    const hasChanges = JSON.stringify(currentItems) !== JSON.stringify(this.initialItems);
    this.hasChanges.emit(hasChanges);
  }
  
  private saveInitialState(items: SurveyListOption[]) {
    this.initialItems = JSON.parse(JSON.stringify(items));
    this.checkForChanges();
  }
  resetChangeTracking() {
    this.saveInitialState(this.items());
  }
  public async reloadItems(listId: string) {
    try {
      const response = await firstValueFrom(
        this.listOptionService.getListOptions(listId)
      );
      const sortedItems = (response || []).sort(
        (a, b) => (a.option_key ?? 0) - (b.option_key ?? 0)
      );
      this.items.set(sortedItems);
      this.saveInitialState(sortedItems);
      this.activeItemId.set(null);
      this.form.reset();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error reloading items:', error);
    }
  }
  public refreshCurrentList() {
    const currentListId = this.activeListId();
    if (currentListId) {
      this.reloadItems(currentListId);
    }
  }

  readonly dialog = inject(MatDialog);
  async addBulkOptions() {
    const listId = this.activeListId();
    if (!listId) return;
  
    const dialogRef = this.dialog.open(AddBulkOptionsDialog, { width: '500px' });
  
    dialogRef.afterClosed().subscribe(async (result: string) => {
      if (result) {
        // Split by new line and filter out empty strings
        const lines = result.split('\n').map(s => s.trim()).filter(s => s.length > 0);
        
        try {
          // Update your service call to pass the options
          const response = await firstValueFrom(
            this.listOptionService.createBulkOptions(listId, lines)
          );
          
          if (response) {
            await this.reloadItems(listId); // Refresh the UI with new items
            this.listOptionService.notifyListUpdated(listId);
          }
        } catch (error) {
          console.error('Error creating bulk options:', error);
        }
      }
    });
  }
}
